import { api } from './api.js';
import type { AuthResponse, AuthTokens, LoginInput, LoginResponse } from '@elmes/shared';
import type { ApiResponse } from '@elmes/shared';

interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface Setup2faResult {
  otpauthUri: string;
  base32: string;
  recoveryCodes: string[];
}

export const authService = {
  async login(data: LoginInput) {
    const res = await api.post<ApiResponse<LoginResponse>>('/auth/login', data);
    return res.data.data!;
  },

  async loginVerify(challengeToken: string, code: string) {
    const res = await api.post<ApiResponse<AuthResponse>>('/auth/login-verify', { challengeToken, code });
    return res.data.data!;
  },

  async register(data: RegisterInput) {
    const res = await api.post<ApiResponse<AuthResponse>>('/auth/register', data);
    return res.data.data!;
  },

  async me() {
    const res = await api.get<ApiResponse>('/auth/me');
    return res.data.data;
  },

  async logout() {
    await api.post('/auth/logout');
  },

  // Roadmap #44 — Forgot Password + Email Verification
  async forgotPassword(email: string) {
    const res = await api.post<ApiResponse>('/auth/forgot-password', { email });
    return res.data;
  },

  async resetPassword(token: string, password: string) {
    const res = await api.post<ApiResponse>('/auth/reset-password', { token, password });
    return res.data;
  },

  /** The own password; every other session ends and the returned pair replaces this one's. */
  async changePassword(currentPassword: string, newPassword: string) {
    const res = await api.post<ApiResponse<AuthTokens>>('/auth/change-password', { currentPassword, newPassword });
    return res.data.data!;
  },

  async verifyEmail(token: string) {
    const res = await api.post<ApiResponse>('/auth/verify-email', { token });
    return res.data;
  },

  async resendVerification() {
    const res = await api.post<ApiResponse>('/auth/resend-verification');
    return res.data;
  },

  // Roadmap #45 — TOTP 2FA
  async setup2fa() {
    const res = await api.post<ApiResponse<Setup2faResult>>('/auth/2fa/setup');
    return res.data.data!;
  },

  async enable2fa(code: string) {
    const res = await api.post<ApiResponse>('/auth/2fa/enable', { code });
    return res.data;
  },

  async disable2fa(password: string, code: string) {
    const res = await api.post<ApiResponse>('/auth/2fa/disable', { password, code });
    return res.data;
  },

  async regenerateRecoveryCodes(password: string) {
    const res = await api.post<ApiResponse<{ recoveryCodes: string[] }>>('/auth/2fa/recovery/regenerate', { password });
    return res.data.data!.recoveryCodes;
  },

  async adminDisable2fa(userId: number) {
    const res = await api.post<ApiResponse>(`/auth/2fa/admin-disable/${userId}`);
    return res.data;
  },

  // Roadmap #46 — OAuth Providers
  async oauthAvailableProviders() {
    const res = await api.get<ApiResponse<{ providers: ('google' | 'github')[] }>>('/auth/oauth/providers');
    return res.data.data!.providers;
  },

  oauthStartUrl(provider: 'google' | 'github', intent: 'login' | 'connect' = 'login', next?: string) {
    const params = new URLSearchParams({ intent });
    if (next) params.set('next', next);
    // GET-redirect endpoint — exposed via api baseURL prefix.
    return `${(api.defaults.baseURL ?? '/api').replace(/\/$/, '')}/auth/oauth/${provider}/start?${params.toString()}`;
  },

  async oauthRedeemHandoff(handoff: string) {
    const res = await api.post<ApiResponse<{ user: any; tokens: { accessToken: string; refreshToken: string } }>>(
      '/auth/oauth/handoff',
      { handoff },
    );
    return res.data.data!;
  },

  async oauthListLinked() {
    const res = await api.get<
      ApiResponse<{
        links: Array<{
          provider: 'google' | 'github';
          providerEmail: string | null;
          displayName: string | null;
          avatarUrl: string | null;
          createdAt: string;
        }>;
        available: ('google' | 'github')[];
      }>
    >('/auth/oauth/links');
    return res.data.data!;
  },

  async oauthUnlink(provider: 'google' | 'github') {
    const res = await api.delete<ApiResponse>(`/auth/oauth/${provider}`);
    return res.data;
  },
};
